import { ArrowRight, ArrowLeft, Shield, Code, Search, Lock, CheckCircle, AlertCircle, FileCode, Package, Target, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from 'react';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from "@/components/ui/card";
import SectionDivider from '@/components/SectionDivider';
import SEO from '@/components/SEO';
import StructuredData from '@/components/StructuredData';

const ApplicationSecurity = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeService, setActiveService] = useState(0);

  const applicationServices = [
    {
      icon: <Code className="w-8 h-8" />,
      title: "Static Application Security Testing (SAST)",
      subtitle: "White-box source code analysis for early vulnerability detection",
      description: "Analyze source code to identify vulnerabilities before they reach production through comprehensive static analysis and automated scanning.",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      borderColor: "rgba(59, 130, 246, 0.3)",
      iconColor: "#3b82f6",
      capabilities: [
        {
          title: "White-box Source Code Analysis",
          description: "Comprehensive source code vulnerability identification and analysis"
        },
        {
          title: "Early Vulnerability Detection",
          description: "Development-stage security issue identification before production"
        },
        {
          title: "IDE Integration Support",
          description: "Real-time security feedback within development environments"
        },
        {
          title: "Automated CI/CD Pipeline Scanning",
          description: "Continuous security testing integrated into build processes"
        },
        {
          title: "Custom Rule Development",
          description: "Tailored security rules for organization-specific requirements"
        },
        {
          title: "Multi-language Support",
          description: "Security analysis across diverse programming languages and frameworks"
        }
      ]
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Dynamic Application Security Testing (DAST)",
      subtitle: "Black-box runtime testing for comprehensive vulnerability assessment",
      description: "Test running applications to find runtime vulnerabilities and configuration issues through comprehensive black-box testing approaches.",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      borderColor: "rgba(16, 185, 129, 0.3)",
      iconColor: "#10b981",
      capabilities: [
        {
          title: "Black-box Runtime Testing",
          description: "External application vulnerability assessment without source code access"
        },
        {
          title: "API Security Testing",
          description: "REST and GraphQL API endpoint security validation and testing"
        },
        {
          title: "Authentication & Authorization Testing",
          description: "Access control and privilege escalation vulnerability assessment"
        },
        {
          title: "Business Logic Validation",
          description: "Application workflow and business rule security testing"
        },
        {
          title: "Configuration Security Assessment",
          description: "Server and application configuration vulnerability analysis"
        },
        {
          title: "Input Validation Testing",
          description: "Data sanitization and injection vulnerability testing"
        }
      ]
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: "Software Composition Analysis (SCA)",
      subtitle: "Dependency risk management and supply chain security",
      description: "Identify and manage security risks in open source dependencies through comprehensive dependency analysis and vulnerability management.",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      borderColor: "rgba(139, 92, 246, 0.3)",
      iconColor: "#8b5cf6",
      capabilities: [
        {
          title: "Dependency Vulnerability Scanning",
          description: "Open source and third-party component security vulnerability analysis"
        },
        {
          title: "License Compliance Checking",
          description: "Software license risk assessment and compliance validation"
        },
        {
          title: "Automated Patch Management",
          description: "Continuous dependency updates and security patch automation"
        },
        {
          title: "Supply Chain Security Monitoring",
          description: "Third-party component integrity and security monitoring"
        },
        {
          title: "Outdated Component Identification",
          description: "Legacy dependency detection and upgrade recommendations"
        },
        {
          title: "Policy Enforcement & Governance",
          description: "Dependency usage policies and organizational governance frameworks"
        }
      ]
    },
    {
      icon: <FileCode className="w-8 h-8" />,
      title: "Secure Code Review",
      subtitle: "Expert manual analysis by senior security engineers",
      description: "Expert manual review of critical code sections by senior security engineers with deep expertise in application security.",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      borderColor: "rgba(245, 158, 11, 0.3)",
      iconColor: "#f59e0b",
      capabilities: [
        {
          title: "Business Logic Security Review",
          description: "Application workflow and business rule security analysis by experts"
        },
        {
          title: "Architecture Security Assessment",
          description: "System design and component interaction security evaluation"
        },
        {
          title: "Cryptographic Implementation Review",
          description: "Encryption and cryptographic control implementation validation"
        },
        {
          title: "Security Best Practices Guidance",
          description: "Industry-standard secure coding practices and recommendations"
        },
        {
          title: "Threat Modeling Integration",
          description: "Application-specific threat model development and validation"
        },
        {
          title: "Custom Security Control Evaluation",
          description: "Organization-specific security control effectiveness assessment"
        }
      ]
    }
  ];

  const benefits = [
    {
      icon: <AlertCircle className="w-12 h-12" />,
      title: "Early Detection",
      description: "Find vulnerabilities in development when they're 100x cheaper to fix",
      color: "#f59e0b"
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Comprehensive Coverage",
      description: "Multi-layered testing approach ensures no security blind spots",
      color: "#8b5cf6"
    },
    {
      icon: <Lock className="w-12 h-12" />,
      title: "Expert Guidance",
      description: "Senior security engineers guide your team through remediation",
      color: "#10b981"
    }
  ];

  return (
    <PageLayout>
      <SEO
        title="Application Security Testing | SAST, DAST, SCA & Secure Code Review"
        description="Expert application security testing services including Static Analysis (SAST), Dynamic Testing (DAST), Software Composition Analysis (SCA), and secure code review. Protect your applications with comprehensive security testing by certified security experts."
        keywords={[
          'application security testing',
          'SAST testing',
          'DAST testing',
          'SCA testing',
          'secure code review',
          'application security services',
          'static application security testing',
          'dynamic application security testing',
          'software composition analysis',
          'application vulnerability assessment',
          'secure software development',
          'application security consulting',
          'web application security testing',
          'mobile application security',
          'API security testing'
        ]}
      />
      <StructuredData type="appSecurity" />
      
      {/* Hero Section */}
      <section
        className="pt-24 pb-16 relative overflow-hidden"
        style={{
          background: "#0f0f17",
          color: "#fff",
        }}
      >
        {/* Animated background gradient */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 30%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 70%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.1) 0%, transparent 70%)" }} />
        </div>

        <div className="container mx-auto px-8 relative z-10" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <Link
              to="/services"
              className="inline-flex items-center mb-8 transition-colors group rounded-full px-5 py-2 font-semibold shadow-lg"
              style={{
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: "999px",
                boxShadow: "0 4px 24px 0 rgba(59, 130, 246, 0.18)"
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Services
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div
                className="inline-block mb-4 px-6 py-3 text-sm font-medium border shadow-lg"
                style={{
                  background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                  color: "#ffffff",
                  borderRadius: "999px",
                  borderColor: "rgba(59, 130, 246, 0.3)",
                  borderWidth: 1.5,
                  boxShadow: "0 4px 24px 0 rgba(59, 130, 246, 0.25)",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
              >
                <Shield className="w-4 h-4 inline mr-2" />
                Application Security
              </div>
              <h1
                className="mb-6"
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 3.75rem)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: "#fff",
                  letterSpacing: "-0.025em",
                }}
              >
                <span
                  className="block"
                  style={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "#3b82f6",
                    fontWeight: 700,
                  }}
                >
                  Secure Your Applications
                </span>
                <span className="block mt-2" style={{ fontSize: "0.7em", color: "#e5e7eb" }}>
                  From Code to Cloud
                </span>
              </h1>
              <p
                className="max-w-3xl mx-auto"
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                Comprehensive application security testing that covers every layer of your stack.
                Our senior experts ensure your applications are resilient against modern threats.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section
        className="py-16 px-8"
        style={{
          background: "#0f0f17",
        }}
      >
        <div className="container mx-auto" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2
                style={{
                  fontSize: "2.25rem",
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "-0.01em",
                  marginBottom: 16,
                }}
              >
                Comprehensive Application Security Services
              </h2>
              <p
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  maxWidth: 800,
                  margin: "0 auto",
                }}
              >
                From static analysis to runtime testing and dependency management
              </p>
            </motion.div>

            {/* Service Navigation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {applicationServices.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`px-3 py-4 rounded-lg font-medium transition-all duration-300 text-center ${
                    activeService === index ? 'shadow-lg' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    background: activeService === index ? service.gradient : 'rgba(15, 15, 23, 0.85)',
                    border: `1.5px solid ${activeService === index ? service.borderColor : 'rgba(255, 255, 255, 0.1)'}`,
                    color: "#fff",
                    minHeight: "100px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "16px 12px"
                  }}
                >
                  <div style={{ flexShrink: 0 }}>
                    {service.icon}
                  </div>
                  <span className="text-xs sm:text-sm leading-tight" style={{ wordBreak: "break-word" }}>
                    {service.title}
                  </span>
                </button>
              ))}
            </div>

            {/* Active Service Details */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <Card
                  className="mb-8"
                  style={{
                    background: "rgba(15, 15, 23, 0.85)",
                    border: `2px solid ${applicationServices[activeService].borderColor}`,
                    borderRadius: 16,
                  }}
                >
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row items-start mb-8">
                      <div
                        className="flex items-center justify-center mb-4 sm:mb-0 sm:mr-6 flex-shrink-0"
                        style={{
                          width: 64,
                          height: 64,
                          background: applicationServices[activeService].gradient,
                          borderRadius: 16,
                        }}
                      >
                        {applicationServices[activeService].icon}
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-xl sm:text-2xl md:text-3xl"
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            marginBottom: 8,
                          }}
                        >
                          {applicationServices[activeService].title}
                        </h3>
                        <p
                          className="text-base sm:text-lg"
                          style={{
                            color: applicationServices[activeService].iconColor,
                            fontWeight: 600,
                            marginBottom: 16,
                          }}
                        >
                          {applicationServices[activeService].subtitle}
                        </p>
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.7,
                          }}
                        >
                          {applicationServices[activeService].description}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {applicationServices[activeService].capabilities.map((capability, idx) => (
                        <div
                          key={idx}
                          className="p-4 sm:p-6 rounded-lg"
                          style={{
                            background: "rgba(0, 0, 0, 0.3)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          <div className="flex items-start">
                            <Target
                              className="w-5 h-5 mr-3 mt-1 flex-shrink-0"
                              style={{ color: applicationServices[activeService].iconColor }}
                            />
                            <div className="flex-1">
                              <h4
                                className="text-base sm:text-lg"
                                style={{
                                  fontWeight: 600,
                                  color: "#fff",
                                  marginBottom: 8,
                                }}
                              >
                                {capability.title}
                              </h4>
                              <p
                                className="text-xs sm:text-sm"
                                style={{
                                  color: "#9ca3af",
                                  lineHeight: 1.6,
                                }}
                              >
                                {capability.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Benefits Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div 
                    className="flex items-center justify-center mx-auto mb-4" 
                    style={{ 
                      color: benefit.color,
                      width: "48px",
                      height: "48px"
                    }}
                  >
                    {benefit.icon}
                  </div>
                  <h4 style={{ fontWeight: 700, color: "#fff", marginBottom: 8, fontSize: 20 }}>
                    {benefit.title}
                  </h4>
                  <p style={{ color: "#9ca3af", fontSize: 15, lineHeight: 1.6 }}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* Pricing & ROI Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              <div className="text-center p-6 rounded-lg" style={{ background: "rgba(15, 15, 23, 0.85)", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                <h4 style={{ fontWeight: 700, color: "#3b82f6", marginBottom: 8, fontSize: 18 }}>
                  Investment Range
                </h4>
                <p style={{ color: "#fff", fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                  $15,000 - $50,000
                </p>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>
                  Based on application complexity and scope
                </p>
              </div>
              
              <div className="text-center p-6 rounded-lg" style={{ background: "rgba(15, 15, 23, 0.85)", border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                <h4 style={{ fontWeight: 700, color: "#10b981", marginBottom: 8, fontSize: 18 }}>
                  Timeline
                </h4>
                <p style={{ color: "#fff", fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                  4-8 Weeks
                </p>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>
                  From initiation to final remediation guidance
                </p>
              </div>
              
              <div className="text-center p-6 rounded-lg" style={{ background: "rgba(15, 15, 23, 0.85)", border: "1px solid rgba(245, 158, 11, 0.2)" }}>
                <h4 style={{ fontWeight: 700, color: "#f59e0b", marginBottom: 8, fontSize: 18 }}>
                  ROI Impact
                </h4>
                <p style={{ color: "#fff", fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                  85% Reduction
                </p>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>
                  Average vulnerability reduction achieved
                </p>
              </div>
            </motion.div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-16 text-center"
              style={{
                background: "rgba(15, 15, 23, 0.85)",
                borderRadius: 16,
                padding: "3rem",
                border: "1px solid rgba(59, 130, 246, 0.2)",
              }}
            >
              <h3
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#fff",
                  marginBottom: 16,
                }}
              >
                Secure Your Applications Today
              </h3>
              <p
                style={{
                  color: "#d1d5db",
                  fontSize: 18,
                  marginBottom: 32,
                  maxWidth: 600,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                Don't wait for a breach. Let our experts secure your applications before attackers find vulnerabilities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contactus"
                  className="inline-flex items-center font-medium transition-all shadow-lg hover:shadow-xl"
                  style={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                    color: "#ffffff",
                    padding: "1rem 2rem",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    boxShadow: "0 4px 24px 0 rgba(59, 130, 246, 0.25)",
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)";
                  }}
                >
                  Get Security Assessment
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center font-medium transition-all"
                  style={{
                    background: "transparent",
                    color: "#3b82f6",
                    padding: "1rem 2rem",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    border: "1.5px solid #3b82f6",
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = "rgba(59, 130, 246, 0.1)";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  View All Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default ApplicationSecurity;